import { useMemo } from 'react';
import { useList } from '@refinedev/core';
import { Users, AlertTriangle } from 'lucide-react';
import type { SegmentDefinition } from '@masters-club/validation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SegmentBuilder, AudienceUsersTable } from '@/components/audience-segment';
import { isBroadcastEvent, type TriggerEventType } from '../../types';

const EMPTY_DEFINITION: SegmentDefinition = { groupsOp: 'AND', groups: [] };

interface SavedSegment {
  id: number;
  name: string;
  description: string | null;
  mode: 'DYNAMIC' | 'STATIC';
}

interface AudienceStepProps {
  eventType: TriggerEventType | null;
  audienceDefinition: SegmentDefinition | null;
  audienceSegmentId: number | null;
  onAudienceDefinitionChange: (def: SegmentDefinition | null) => void;
  onAudienceSegmentIdChange: (id: number | null) => void;
  disabled?: boolean;
}

/**
 * AudienceStep — единый шаг настройки аудитории триггера.
 *
 * Логика:
 *   - Переключатель «Использовать сохранённый сегмент»:
 *       OFF (default) → SegmentBuilder для inline-условий.
 *       ON → выбор UserSegment из списка; inline-условия очищаются.
 *   - Снизу — AudienceUsersTable с предпросмотром аудитории (как на трансляциях).
 *   - Для broadcast-событий (NEW_COURSE_AVAILABLE и т.п.) — обязательное предупреждение,
 *     что без аудитории триггер никому не отправит.
 */
export function AudienceStep({
  eventType,
  audienceDefinition,
  audienceSegmentId,
  onAudienceDefinitionChange,
  onAudienceSegmentIdChange,
  disabled = false,
}: AudienceStepProps) {
  const useSavedSegment = audienceSegmentId !== null;

  const { result: segmentsResult } = useList<SavedSegment>({
    resource: 'audience-segments',
    pagination: { pageSize: 200, mode: 'server' },
  });

  // Защита от рассинхрона формы ответа: data-provider должен отдать массив,
  // но если эндпоинт вернёт обёртку — не роняем шаг белым экраном.
  const savedSegments = useMemo(() => {
    const raw = segmentsResult?.data;
    return Array.isArray(raw) ? raw : [];
  }, [segmentsResult]);

  const selectedSegment = useMemo(
    () => savedSegments.find((s) => s.id === audienceSegmentId) ?? null,
    [savedSegments, audienceSegmentId],
  );

  // Определение, которое уйдёт в AudienceUsersTable для preview.
  // Если выбран сохранённый сегмент — берём его definition (через отдельный запрос),
  // если inline — берём audienceDefinition.
  const { result: segmentDetail } = useList<{ id: number; definition: SegmentDefinition }>({
    resource: 'audience-segments',
    filters: audienceSegmentId !== null
      ? [{ field: 'id', operator: 'eq', value: audienceSegmentId }]
      : [],
    queryOptions: { enabled: audienceSegmentId !== null },
  });

  const previewDefinition: SegmentDefinition = useMemo(() => {
    if (useSavedSegment) {
      const detail = segmentDetail?.data?.[0];
      return detail?.definition ?? EMPTY_DEFINITION;
    }
    return audienceDefinition ?? EMPTY_DEFINITION;
  }, [useSavedSegment, segmentDetail, audienceDefinition]);

  const handleToggleSaved = (checked: boolean) => {
    if (checked) {
      // Переключились на сохранённый — очищаем inline.
      onAudienceDefinitionChange(null);
      // ID выберется через select; пока null, table покажет "всех".
    } else {
      onAudienceSegmentIdChange(null);
      // Если inline пуст — оставим пустую заготовку, чтобы builder не падал.
      if (audienceDefinition === null) {
        onAudienceDefinitionChange(EMPTY_DEFINITION);
      }
    }
  };

  const handleSegmentSelect = (value: string) => {
    if (value === '__none__') {
      onAudienceSegmentIdChange(null);
      return;
    }
    const id = Number(value);
    if (Number.isFinite(id)) {
      onAudienceSegmentIdChange(id);
    }
  };

  const broadcast = isBroadcastEvent(eventType);
  const hasAudience =
    audienceSegmentId !== null ||
    (audienceDefinition !== null && audienceDefinition.groups.length > 0);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Аудитория триггера
          </CardTitle>
          <CardDescription>
            {broadcast
              ? 'Это broadcast-событие — рассылается всем пользователям, попадающим под условия сегмента. Без указанной аудитории триггер не сработает ни для кого.'
              : 'Условия дополнительно фильтруют пользователя из события. Без условий триггер срабатывает для каждого подходящего пользователя.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {broadcast && !hasAudience && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="text-sm">
                Для broadcast-события нужна аудитория. Сейчас триггер настроен «никому».
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Switch
              id="trigger-audience-saved-toggle"
              checked={useSavedSegment}
              onCheckedChange={handleToggleSaved}
              disabled={disabled}
            />
            <Label
              htmlFor="trigger-audience-saved-toggle"
              className="text-sm cursor-pointer"
            >
              Использовать сохранённый сегмент
            </Label>
          </div>

          {useSavedSegment ? (
            <div className="space-y-2">
              <Label>Сегмент</Label>
              <Select
                value={audienceSegmentId !== null ? String(audienceSegmentId) : '__none__'}
                onValueChange={handleSegmentSelect}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите сегмент…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— не выбрано —</SelectItem>
                  {savedSegments.map((seg) => (
                    <SelectItem key={seg.id} value={String(seg.id)}>
                      {seg.name}
                      {seg.mode === 'STATIC' ? ' (снимок)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSegment?.description && (
                <p className="text-xs text-muted-foreground">{selectedSegment.description}</p>
              )}
            </div>
          ) : (
            <SegmentBuilder
              value={audienceDefinition ?? EMPTY_DEFINITION}
              onChange={(next) => onAudienceDefinitionChange(next)}
              disabled={disabled}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            На кого сработает
          </CardTitle>
          <CardDescription>
            Предпросмотр пользователей, удовлетворяющих условиям аудитории. Триггер выполнится
            для каждого из них при наступлении события.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AudienceUsersTable
            previewUrl="/audience/preview"
            definition={previewDefinition}
            disabled={disabled}
            showMatchColumn={hasAudience}
            matchColumnHeader="Триггер сработает"
            matchTrueLabel="Да"
            matchFalseLabel="Нет"
          />
        </CardContent>
      </Card>
    </div>
  );
}
